import { useState } from 'react'
import { Check, Copy } from 'lucide-react'
import { toast } from 'sonner'

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/components/ui/dialog'
import { Button } from '@/shared/components/ui/button'
import type { ApiKey } from '@/admin/types/api-keys'

interface RevealApiKeyDialogProps {
  open: boolean
  onClose: () => void
  /** The freshly-minted secret, shown exactly once (creation or rotation). */
  rawToken?: string
  /** The key being shown, for its name/description and masked display. */
  apiKey?: ApiKey | null
}

export function RevealApiKeyDialog({ open, onClose, rawToken, apiKey }: RevealApiKeyDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(value) => { if (!value) onClose() }}>
      <DialogContent className="max-w-lg">
        {open && (
          <RevealApiKeyContent rawToken={rawToken} apiKey={apiKey} onClose={onClose} />
        )}
      </DialogContent>
    </Dialog>
  )
}

function RevealApiKeyContent({
  rawToken,
  apiKey,
  onClose,
}: {
  rawToken?: string
  apiKey?: ApiKey | null
  onClose: () => void
}) {
  const [copied, setCopied] = useState(false)

  function handleCopy() {
    if (!rawToken) return
    navigator.clipboard.writeText(rawToken)
    setCopied(true)
    toast.success('API key copied to clipboard')
    setTimeout(() => setCopied(false), 2000)
  }

  const displayName = apiKey?.name ?? 'New API Key'
  const displayDescription = apiKey?.description ?? ''

  return (
    <>
      <DialogHeader>
        <DialogTitle>API Key</DialogTitle>
      </DialogHeader>

      <div className="space-y-4">
        <div>
          <p className="text-[14px] font-medium text-[var(--ink)]">{displayName}</p>
          {displayDescription && (
            <p className="mt-0.5 text-[12px] text-[var(--ink-3)]">{displayDescription}</p>
          )}
        </div>

        {rawToken ? (
          <div className="rounded-lg bg-[var(--bg-subtle)] p-4">
            <code className="block break-all font-mono text-[13px] text-[var(--ink)]">
              {rawToken}
            </code>
          </div>
        ) : (
          <div className="rounded-lg bg-[var(--bg-subtle)] p-4">
            <code className="block break-all font-mono text-[13px] text-[var(--ink-3)]">
              {apiKey?.masked_display ?? ''}
            </code>
          </div>
        )}

        <p className="text-[11px] text-[var(--ink-3)]">
          {rawToken
            ? 'This is the only time the full key is shown. Store it now - it cannot be retrieved again. Lost it? Rotate the key to issue a new one.'
            : 'The full key is only shown once, at creation. Rotate the key to issue a new secret.'}
        </p>
      </div>

      <DialogFooter className="border-t-[0.5px] border-t-[var(--rule)] pt-3">
        <Button variant="outline" onClick={onClose}>
          Close
        </Button>
        <Button onClick={handleCopy} disabled={!rawToken}>
          {copied ? (
            <Check className="mr-1.5 h-3.5 w-3.5" />
          ) : (
            <Copy className="mr-1.5 h-3.5 w-3.5" />
          )}
          {copied ? 'Copied' : 'Copy to clipboard'}
        </Button>
      </DialogFooter>
    </>
  )
}
